import {useEffect, useRef} from 'react'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useGenerateChangeGroupsMutation} from '../mutations/use-generate-change-groups-mutation'
import {
  useCanAccessChangeGroups,
  useChangeGroups,
  useHasCommitRange,
  useLatestChangeGroupOrchestration,
} from '../stores/selectors'
import {isErrorState, isCompletedChangeGroupState} from '../helpers/change-groups'

interface UseAutoGenerateChangeGroupsOptions {
  /**
   * Whether change groups feature is enabled for this component
   */
  changeGroupsEnabled: boolean
}

/**
 * Custom hook that automatically generates change groups on mount when conditions are met.
 * This hook will only trigger the generation once per component lifecycle.
 * @param changeGroupsEnabled - Flag indicating if change groups feature is enabled for this component
 */
export function useAutoGenerateChangeGroups({changeGroupsEnabled}: UseAutoGenerateChangeGroupsOptions) {
  const canAccessChangeGroups = useCanAccessChangeGroups()
  const changeGroups = useChangeGroups()
  const hasChangeGroups = Boolean(changeGroups && changeGroups.length > 0)
  const manuallyTriggerChangeGroupsEnabled = useFeatureFlag('generate_groups_on_files_view')
  const {mutate: generateChangeGroups} = useGenerateChangeGroupsMutation()
  const changeGroupOrchestration = useLatestChangeGroupOrchestration()
  const isErrorOrIncomplete =
    changeGroupOrchestration &&
    (isErrorState(changeGroupOrchestration.state) || !isCompletedChangeGroupState(changeGroupOrchestration.state))
  const hasCommitRange = useHasCommitRange()
  const hasTriggeredRef = useRef(false)

  useEffect(() => {
    // Only trigger generation once when all conditions are met, including
    // the orchestration is not in an error or incomplete state
    // groups are not already present
    // feature flag is enabled and user can access change groups
    // a commit range is not selected
    if (
      !hasTriggeredRef.current &&
      manuallyTriggerChangeGroupsEnabled &&
      changeGroupsEnabled &&
      canAccessChangeGroups &&
      !hasChangeGroups &&
      !hasCommitRange &&
      !isErrorOrIncomplete
    ) {
      generateChangeGroups()
      hasTriggeredRef.current = true
    }
  }, [
    canAccessChangeGroups,
    manuallyTriggerChangeGroupsEnabled,
    generateChangeGroups,
    hasChangeGroups,
    isErrorOrIncomplete,
    hasCommitRange,
    changeGroupsEnabled,
  ])
}
